import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import queryString from 'query-string';

import {
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
} from 'common/householdConstants';

import { isApplicationPastStatus } from 'common/householdApplication/applicationHelpers';

import { getStrongestApplicationStatus } from 'common/householdHelpers';

import PartnerActions from 'client/actions/Partner';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrPartnerList from 'client/components/SrPartnerList';
import SrPartnerSection from 'client/components/SrPartnerSection';
import { SR_BUTTON_THEME_ADOBE_GREY } from 'client/components/SrButton/SrButton';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrPartnerHouseholdPendingApprovalCard from 'client/components/SrPartnerHouseholdPendingApprovalCard';
import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import SrArchivedHouseholdCard from 'client/components/SrArchivedHouseholdCard/SrArchivedHouseholdCard';

import './SrPartnerListingHouseholdsApplying.less';

const getPendingApprovalId = (location) => {
  const locationQuery = queryString.parse(location.search) || {};
  return locationQuery.pendingApprovalId ? parseInt(locationQuery.pendingApprovalId, 10) : null;
};

const SrPartnerListingHouseholdsApplying = ({
  partner,
  listingId,
  householdsApplying,
  appUrl,
  history,
  dispatch,
  currentUser,
  location,
}) => {
  const [showArchivedApplicationsModal, setShowArchivedApplicationsModal] = useState(false);
  const {
    nextQualifiedHousehold,
    pendingHouseholds,
    pendingHouseholdsCount,
    deniedHouseholds,
    deniedHouseholdsCount,
  } = householdsApplying;

  const households = [nextQualifiedHousehold, ...pendingHouseholds].filter(
    (household) => !!household,
  );

  const { householdsInReview, householdsCompleted } = households.reduce(
    (memo, household) => {
      const applicationStatus = getStrongestApplicationStatus({
        householdApplications: household.householdApplications,
      });

      if (
        isApplicationPastStatus(
          { status: applicationStatus },
          HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
        )
      ) {
        memo.householdsInReview.push(household);
      } else if (applicationStatus === HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD) {
        memo.householdsInReview.push(household);
      } else {
        memo.householdsCompleted.push(household);
      }

      return memo;
    },
    {
      householdsAccepted: [],
      householdsInReview: [],
      householdsCompleted: [],
    },
  );

  useEffect(() => {
    if (location) {
      const pendingApprovalId = getPendingApprovalId(location);
      if (pendingApprovalId) {
        const element = global.document.querySelector(`.pendingApprovalId-${pendingApprovalId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [location]);

  if (!nextQualifiedHousehold && !pendingHouseholdsCount && !deniedHouseholdsCount) {
    return null;
  }

  const strongestHouseholdApplicationStatus =
    nextQualifiedHousehold &&
    getStrongestApplicationStatus({
      householdApplications: nextQualifiedHousehold.householdApplications,
    });
  const nextQualifiedHouseholdWasAccepted =
    nextQualifiedHousehold &&
    isApplicationPastStatus(
      { status: strongestHouseholdApplicationStatus },
      HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
    );

  const onHouseholdCardChange = () => {
    dispatch(PartnerActions.getHouseholdsApplyingToListing, {
      listingId: listingId,
      forceRefresh: true,
    });
  };

  return (
    <>
      {householdsInReview.length > 0 && (
        <SrPartnerSection title="Ready for Review">
          <SrPartnerList>
            {householdsInReview.map((household) => (
              <SrPartnerHouseholdPendingApprovalCard
                key={household.id}
                title={
                  nextQualifiedHouseholdWasAccepted
                    ? `Approved Application #${household.id}`
                    : `Application #${household.id}`
                }
                concession={household.concession}
                household={household}
                moveInDate={household.moveInDate}
                householdListing={household.householdListing}
                listing={household.listing}
                tenantScreeningCriteria={household.tenantScreeningCriteria}
                renters={household.renters}
                householdApplications={household.householdApplications}
                householdGuarantors={household.householdGuarantors}
                guarantorApplications={household.guarantorApplications}
                tenantAlertReports={household.tenantAlertReports}
                breedRestrictions={household.breedRestrictions}
                partner={partner}
                history={history}
                currentUser={currentUser}
                appUrl={appUrl}
                onApprove={onHouseholdCardChange}
                onDeny={onHouseholdCardChange}
                leaseStartDate={household.leaseStartDate}
                pendingLandlordDate={household.pendingLandlordDate}
                hasMoreActiveApps={household.activeApps > 0}
              />
            ))}
          </SrPartnerList>
        </SrPartnerSection>
      )}

      {(householdsCompleted.length > 0 || deniedHouseholdsCount > 0) && (
        <SrPartnerSection title="Processing Applications">
          <SrPartnerList>
            {householdsCompleted.map((household) => (
              <SrPartnerHouseholdPendingApprovalCard
                key={household.id}
                title={`Application #${household.id}`}
                concession={household.concession}
                household={household}
                moveInDate={household.moveInDate}
                householdListing={household.householdListing}
                listing={household.listing}
                tenantScreeningCriteria={household.tenantScreeningCriteria}
                renters={household.renters}
                householdApplications={household.householdApplications}
                householdGuarantors={household.householdGuarantors}
                guarantorApplications={household.guarantorApplications}
                tenantAlertReports={household.tenantAlertReports}
                breedRestrictions={household.breedRestrictions}
                partner={partner}
                history={history}
                currentUser={currentUser}
                appUrl={appUrl}
                onApprove={onHouseholdCardChange}
                onDeny={onHouseholdCardChange}
                leaseStartDate={household.leaseStartDate}
                pendingLandlordDate={household.pendingLandlordDate}
                hasMoreActiveApps={household.activeApps > 0}
              />
            ))}
          </SrPartnerList>

          <SrPartnerList twoColumns>
            {deniedHouseholdsCount > 0 && (
              <div>
                <SrBox
                  borderRadius="14px"
                  backgroundColor="white"
                  padding="20px 20px 30px 20px"
                  boxShadow="0px 5px 21px rgba(25, 51, 64, 0.05)"
                >
                  <SrText
                    fontSize="26px"
                    font="greycliff"
                    lineHeight="25px"
                    weight="800"
                    display="block"
                    color="grey600"
                  >
                    {deniedHouseholdsCount} Archived{' '}
                    {pluralize('Application', deniedHouseholdsCount)}
                  </SrText>
                  <SrBox marginTop="7px" />
                  <SrText
                    weight="600"
                    display="block"
                    font="greycliff"
                    color="blueGreycliff"
                    fontSize="16px"
                    lineHeight="25px"
                  >
                    Applications you denied, and applicants that decided to not apply anymore.
                  </SrText>
                  <SrBox marginTop="25px" />
                  <SrLabel
                    color="grey600"
                    borderColor="#7B8488"
                    label="See Archived Applications"
                    alignCenter
                    justifyCenter
                    onClick={() => setShowArchivedApplicationsModal(true)}
                  />
                </SrBox>
              </div>
            )}
          </SrPartnerList>
        </SrPartnerSection>
      )}

      {showArchivedApplicationsModal && (
        <SrModalV2
          theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
          buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
          onClose={() => setShowArchivedApplicationsModal(false)}
          overflow="auto"
        >
          <SrFlex direction="col" maxHeight="100%">
            <SrBox>
              <SrText
                font="greycliff"
                padding="0 15px 15px"
                size="30"
                lineHeight="35px"
                weight="bolder"
                display="block"
              >
                Archived Applications
              </SrText>
            </SrBox>
            <SrBox paddingBottom="80px">
              {deniedHouseholds.map((household) => (
                <SrArchivedHouseholdCard
                  key={household.id}
                  household={household}
                  currentUser={currentUser}
                />
              ))}
            </SrBox>
          </SrFlex>
        </SrModalV2>
      )}
    </>
  );
};

SrPartnerListingHouseholdsApplying.propTypes = {
  partner: PropTypes.object.isRequired,
  listingId: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  householdsApplying: PropTypes.object.isRequired,
  appUrl: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

SrPartnerListingHouseholdsApplying.displayName = 'SrPartnerListingHouseholdsApplying';

export default SrPartnerListingHouseholdsApplying;
